import { useZeroSelector } from "@/hooks/use-zero-selector";
import { queries } from "../queries";
import { useCurrentUser } from "../current-user";
import { createIncrementalMapSelector } from "../incremental-map-selector";
import { useMapChurnLog } from "../use-map-churn-log";

/**
 * Per-user presence values. Mirrors the hot columns split out of `users`
 * in Phase 1.5 (~/.claude/plans/zero-perf-rollout.md).
 *
 * Consumers subscribe to `useUserPresenceMap()` instead of pulling these
 * fields off the workspace-wide users subscription. After PR 1.5.5 (drop
 * the reverse trigger) this is the ONLY way to read presence — the
 * `users` table no longer carries hot columns.
 */
export type UserPresence = {
  user_id: string;
  is_online: boolean;
  last_active_at: number | null;
  status: string | null;
  status_emoji: string | null;
  status_expiry: number | null;
  typing_in: string | null;
  typing_at: number | null;
  terminal_process: string | null;
  terminal_cwd: string | null;
  terminal_active_at: number | null;
  terminal_share: boolean;
};

const CACHE_KEY = "user_presence.all";
const EMPTY_PRESENCE_MAP = new Map<string, UserPresence>();

const presenceMapSelector = createIncrementalMapSelector(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (u: any) => u.user_id as string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (u: any): UserPresence => ({
    user_id: u.user_id,
    is_online: !!u.is_online,
    last_active_at: u.last_active_at ?? null,
    status: u.status ?? null,
    status_emoji: u.status_emoji ?? null,
    status_expiry: u.status_expiry ?? null,
    typing_in: u.typing_in ?? null,
    typing_at: u.typing_at ?? null,
    terminal_process: u.terminal_process ?? null,
    terminal_cwd: u.terminal_cwd ?? null,
    terminal_active_at: u.terminal_active_at ?? null,
    terminal_share: !!u.terminal_share,
  }),
  (prev, next) =>
    prev.is_online === next.is_online &&
    prev.last_active_at === next.last_active_at &&
    prev.status === next.status &&
    prev.status_emoji === next.status_emoji &&
    prev.status_expiry === next.status_expiry &&
    prev.typing_in === next.typing_in &&
    prev.typing_at === next.typing_at &&
    prev.terminal_process === next.terminal_process &&
    prev.terminal_cwd === next.terminal_cwd &&
    prev.terminal_active_at === next.terminal_active_at &&
    prev.terminal_share === next.terminal_share,
);

/**
 * Identity-stable Map of presence rows for users the caller can see
 * (workspace-scoped via `userPresenceAccessFilter`). Subscribes to
 * `queries.user_presence.all()`; structurally shared so React only
 * re-renders when a presence value actually changes.
 */
export function useUserPresenceMap(): [
  Map<string, UserPresence>,
  { type: string },
] {
  const { user } = useCurrentUser();
  const result = useZeroSelector(
    CACHE_KEY,
    user ? queries.user_presence.all() : null,
    presenceMapSelector,
    {
      defaultValue: EMPTY_PRESENCE_MAP,
    },
  );
  useMapChurnLog("userPresenceMap", result[0]);
  return result;
}

/**
 * Single-user presence lookup. Stable unless that user's presence row changed.
 */
export function useUserPresence(
  userId: string | undefined,
): UserPresence | undefined {
  const [map] = useUserPresenceMap();
  return userId ? map.get(userId) : undefined;
}

// ─── Status-emoji-only map ──────────────────────────────────────────
//
// Phase 1.5.3 — separate selector that tracks ONLY status_emoji per
// user. Used by useRenderUserMap to merge status_emoji without
// subscribing to the full presence map (which churns on every typing
// heartbeat / last_active_at update). status_emoji changes only on
// explicit status set/clear, so this map's outer reference stays
// stable across typing heartbeats — preserving useRenderUserMap's
// "stable across typing heartbeats" contract.
const STATUS_EMOJI_CACHE_KEY = "user_presence.status_emoji";
const EMPTY_STATUS_EMOJI_MAP = new Map<string, string | null>();

type StatusEmojiEntry = { user_id: string; status_emoji: string | null };

const statusEmojiMapSelector = createIncrementalMapSelector<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  string,
  StatusEmojiEntry
>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (u: any) => u.user_id as string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (u: any): StatusEmojiEntry => ({
    user_id: u.user_id,
    status_emoji: u.status_emoji ?? null,
  }),
  (prev, next) => prev.status_emoji === next.status_emoji,
);

/**
 * Map<user_id, status_emoji>. Outer reference stable across typing
 * heartbeats / last_active_at heartbeats — only changes when an
 * actual status_emoji value changes for some user. useRenderUserMap
 * depends on this instead of useUserPresenceMap to preserve its
 * "stable across typing heartbeats" guarantee.
 */
export function useUserStatusEmojiMap(): [
  Map<string, StatusEmojiEntry>,
  { type: string },
] {
  const { user } = useCurrentUser();
  return useZeroSelector(
    STATUS_EMOJI_CACHE_KEY,
    user ? queries.user_presence.all() : null,
    statusEmojiMapSelector,
    {
      defaultValue: EMPTY_STATUS_EMOJI_MAP as unknown as Map<
        string,
        StatusEmojiEntry
      >,
    },
  );
}
